import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../Dialog'
import { Field } from '../Field'
import { useEntitySave, makeEntity, now } from '../../api/hooks/useEntity'
import { ENTITY, type Grade, type GradeScale, type GradeType, type Student, type Subject } from '../../types/entities'

/** Диалог создания/редактирования одной оценки конкретному ученику. */
export function GradeFormDialog({
  classId,
  students,
  subjects,
  preselectStudentId,
  preselectDate,
  onClose,
}: {
  classId: string
  students: Student[]
  subjects: Subject[]
  preselectStudentId: string
  preselectDate: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const save = useEntitySave<Grade>(ENTITY.grade)
  const [studentId, setStudentId] = useState(preselectStudentId || students[0]?.id || '')
  const [subjectId, setSubjectId] = useState(subjects[0]?.id ?? '')
  const [date, setDate] = useState(preselectDate)
  const [value, setValue] = useState('5')
  const [type, setType] = useState<GradeType>('current')
  const [scale, setScale] = useState<GradeScale>('fivePoint')
  const [weight, setWeight] = useState(1)
  const [comment, setComment] = useState('')
  const [topic, setTopic] = useState('')

  const handleSave = async () => {
    if (!studentId || !subjectId || !value.trim()) return
    await save.mutateAsync(makeEntity<Grade>({
      classId,
      studentId,
      subjectId,
      date,
      value: value.trim(),
      scale,
      type,
      weight,
      comment: comment.trim() || null,
      topic: topic.trim() || null,
      createdAt: now(),
      updatedAt: now(),
    }))
    onClose()
  }

  if (subjects.length === 0) {
    return (
      <Dialog title={t('gradeFormDialog.noSubjectTitle')} onClose={onClose}>
        <p className="text-sm text-gray-600">
          {t('gradeFormDialog.noSubjectText')}
        </p>
        <button onClick={onClose} className="btn-primary text-sm mt-4">{t('gradeFormDialog.ok')}</button>
      </Dialog>
    )
  }

  return (
    <Dialog title={t('gradeFormDialog.title')} onClose={onClose}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('gradeFormDialog.student')}>
          <select className="input-field" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            {students.map((s) => (
              <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>
            ))}
          </select>
        </Field>
        <Field label={t('gradeFormDialog.date')}>
          <input type="date" className="input-field" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      <Field label={t('gradeFormDialog.subject')}>
        <select className="input-field" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('gradeFormDialog.grade')}>
          <input
            className="input-field text-lg font-semibold text-center"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label={t('gradeFormDialog.type')}>
          <select className="input-field" value={type} onChange={(e) => setType(e.target.value as GradeType)}>
            <option value="current">{t('gradeFormDialog.typeCurrent')}</option>
            <option value="test">{t('gradeFormDialog.typeTest')}</option>
            <option value="exam">{t('gradeFormDialog.typeExam')}</option>
            <option value="project">{t('gradeFormDialog.typeProject')}</option>
            <option value="homework">{t('gradeFormDialog.typeHomework')}</option>
            <option value="term">{t('gradeFormDialog.typeTerm')}</option>
            <option value="year">{t('gradeFormDialog.typeYear')}</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('gradeFormDialog.scale')}>
          <select className="input-field" value={scale} onChange={(e) => setScale(e.target.value as GradeScale)}>
            <option value="fivePoint">{t('gradeFormDialog.scaleFive')}</option>
            <option value="tenPoint">{t('gradeFormDialog.scaleTen')}</option>
            <option value="hundredPoint">{t('gradeFormDialog.scaleHundred')}</option>
            <option value="passFail">{t('gradeFormDialog.scalePassFail')}</option>
            <option value="letter">{t('gradeFormDialog.scaleLetter')}</option>
          </select>
        </Field>
        <Field label={t('gradeFormDialog.weight')}>
          <input
            type="number"
            step={0.1}
            min={0.1}
            max={2}
            className="input-field"
            value={weight}
            onChange={(e) => setWeight(parseFloat(e.target.value) || 1)}
          />
        </Field>
      </div>
      <Field label={t('gradeFormDialog.topic')}>
        <input
          className="input-field"
          placeholder={t('gradeFormDialog.topicPlaceholder')}
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />
      </Field>
      <Field label={t('gradeFormDialog.comment')}>
        <textarea className="input-field min-h-[50px]" value={comment} onChange={(e) => setComment(e.target.value)} />
      </Field>
      <div className="flex gap-2 justify-end mt-4">
        <button onClick={onClose} className="btn-outline text-sm">{t('common.cancel')}</button>
        <button
          onClick={handleSave}
          disabled={save.isPending || !studentId || !subjectId || !value.trim()}
          className="btn-primary text-sm"
        >
          {save.isPending ? t('gradeFormDialog.saving') : t('common.save')}
        </button>
      </div>
    </Dialog>
  )
}
